import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { combineLatest } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { UserService } from '../user/user.service';

/**
 * When a store's subscription has expired, non-admin users are confined to the
 * Subscription page so they can renew — every other route redirects there.
 *
 * Used as `canActivateChild` on the main-layout route (covers all its children)
 * AND as `canActivate` on the top-level operational routes that live outside
 * the main layout (sell, cashier, captain-order, kot-display, …). Running on
 * each navigation catches deep-links and in-app navigation, not just load.
 *
 * Confinement follows `isStoreExpired$`, which is true for the store's own
 * users (owners AND platform admins on their own store) and false during admin
 * impersonation — so admins inspecting another store are never confined here.
 * The /admin area and top-level /store, /login routes stay reachable.
 */
export const storeExpiredGuard: CanActivateFn = (route, state) => {
   const userService = inject(UserService);
   const router = inject(Router);

   // Admin area is exempt.
   if (state.url.startsWith('/admin')) return true;

   // The subscription page itself must always stay reachable.
   if (route.routeConfig?.path === 'subscription') return true;

   return combineLatest([
      userService.currentUser$.pipe(filter((user) => user !== null)),
      // Wait for a definitive expiry state (null = still loading).
      userService.isStoreExpired$.pipe(filter((v) => v !== null)),
   ]).pipe(
      take(1),
      map(([, isExpired]) => {
         if (isExpired) {
            return router.createUrlTree(['/subscription']);
         }
         return true;
      }),
   );
};
